// Shared helpers for the client pages: per-client summaries for the list,
// the client colour (with a local fallback), per-client UI preferences and a
// copy of the server's rounding rules for the live preview.

import { clientColor, entryMinutes, normalizeStatus, entryAmount, parseDate } from '../format';
import { prefs } from '../prefs.svelte';
import { isHexColor } from '../theme/palette';
import { resolvePeriod, startOfWeek, addDays, startOfDay, PERIOD_KEYS, type PeriodKey } from '../periods';
import type { Client, RoundingSettings, TimeEntry } from '../types';

/**
 * Blur the focused field so inputs that commit on blur (MoneyInput) store
 * their value before a form is submitted with Enter.
 */
export function commitActiveField() {
  if (typeof document === 'undefined') return;
  (document.activeElement as HTMLElement | null)?.blur?.();
}

/** Clients from older rows may carry 0/1 instead of a boolean. */
export function isActive(client: Pick<Client, 'active'> | null | undefined): boolean {
  const v = client?.active as unknown;
  return !(v === false || v === 0 || v === '0');
}

// ---------- Per-client UI preferences (prefs.value.clientUi[id]) ----------

export interface ClientUi {
  color?: string;
  showTarget?: boolean;
  targetHoursPerWeek?: number | null;
  scenario?: 'conservative' | 'expected' | 'ambitious';
  extraHours?: number;
  rateAdjust?: number;
}

interface ListUi {
  sort?: ClientSort;
  view?: 'grid' | 'table';
}

export function clientUi(id: number): ClientUi {
  return (prefs.value.clientUi?.[String(id)] as ClientUi | undefined) ?? {};
}

export function setClientUi(id: number, patch: Partial<ClientUi>) {
  const all = prefs.value.clientUi || {};
  prefs.update({ clientUi: { ...all, [String(id)]: { ...(all[String(id)] || {}), ...patch } } });
}

export function listUi(): ListUi {
  return (prefs.value.clientUi?._list as ListUi | undefined) ?? {};
}

export function setListUi(patch: Partial<ListUi>) {
  const all = prefs.value.clientUi || {};
  prefs.update({ clientUi: { ...all, _list: { ...(all._list || {}), ...patch } } });
}

/**
 * The client's colour: metadata.color from the server, else the colour picked
 * in this app (kept in the preferences, because PUT /api/clients/:id does not
 * store metadata yet), else a stable colour from lib/theme/palette.
 */
export function colorOf(client: Pick<Client, 'id' | 'metadata'> | null | undefined): string {
  const own = client?.metadata?.color;
  if (isHexColor(own)) return own;
  const local = client ? clientUi(client.id).color : undefined;
  if (isHexColor(local)) return local;
  return clientColor(client);
}

// ---------- Sorting ----------

export type ClientSort = 'name_asc' | 'name_desc' | 'created_at_desc' | 'created_at_asc' | 'hours_month' | 'revenue' | 'outstanding';
export const LEGACY_SORTS: ClientSort[] = ['name_asc', 'name_desc', 'created_at_desc', 'created_at_asc'];
export const ALL_SORTS: ClientSort[] = [...LEGACY_SORTS, 'hours_month', 'revenue', 'outstanding'];

// ---------- Per-client summary for the list ----------

export interface ClientSummary {
  minutesMonth: number;
  revenueMonth: number;
  revenueTotal: number;
  outstanding: number;
  entryCount: number;
  /** Hours per week, oldest first (12 weeks, the current week last). */
  spark: number[];
  lastActivity: string | null;
  firstDate: string | null;
}

const WEEKS = 12;

function empty(): ClientSummary {
  return { minutesMonth: 0, revenueMonth: 0, revenueTotal: 0, outstanding: 0, entryCount: 0, spark: new Array(WEEKS).fill(0), lastActivity: null, firstDate: null };
}

export function summarize(entries: TimeEntry[], weekStart = 1): Map<number, ClientSummary> {
  const out = new Map<number, ClientSummary>();
  const month = resolvePeriod('month');
  const sparkStart = addDays(startOfWeek(new Date(), weekStart), -7 * (WEEKS - 1)).getTime();
  for (const e of entries) {
    if (!e.client_id) continue;
    let s = out.get(e.client_id);
    if (!s) out.set(e.client_id, (s = empty()));
    const d = parseDate(e.date || e.start_time);
    const min = entryMinutes(e);
    const status = normalizeStatus(e.payment_status);
    const amount = status === 'non_billable' ? 0 : entryAmount(e);
    s.entryCount += 1;
    s.revenueTotal += amount;
    if (status === 'open' || status === 'partial') s.outstanding += Math.max(0, amount - (Number(e.amount_paid) || 0));
    if (d) {
      if (d >= month.start && d < month.end) {
        s.minutesMonth += min;
        s.revenueMonth += amount;
      }
      // Days are local midnights; round to absorb daylight-saving shifts.
      const idx = Math.floor(Math.round((startOfDay(d).getTime() - sparkStart) / 86400000) / 7);
      if (idx >= 0 && idx < WEEKS) s.spark[idx] += min / 60;
    }
    const stamp = e.start_time || e.date;
    if (stamp && (!s.lastActivity || stamp > s.lastActivity)) s.lastActivity = stamp;
    if (e.date && (!s.firstDate || e.date < s.firstDate)) s.firstDate = e.date;
  }
  return out;
}

export function sortClients(list: Client[], sort: ClientSort, stats: Map<number, ClientSummary>): Client[] {
  const by = (c: Client) => stats.get(c.id);
  const name = (a: Client, b: Client) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  const created = (c: Client) => c.created_at || '';
  return [...list].sort((a, b) => {
    switch (sort) {
      case 'name_desc': return name(b, a);
      case 'created_at_desc': return created(b).localeCompare(created(a)) || name(a, b);
      case 'created_at_asc': return created(a).localeCompare(created(b)) || name(a, b);
      case 'hours_month': return (by(b)?.minutesMonth || 0) - (by(a)?.minutesMonth || 0) || name(a, b);
      case 'revenue': return (by(b)?.revenueTotal || 0) - (by(a)?.revenueTotal || 0) || name(a, b);
      case 'outstanding': return (by(b)?.outstanding || 0) - (by(a)?.outstanding || 0) || name(a, b);
      default: return name(a, b);
    }
  });
}

/** The work-settings default period, when it is one the picker knows. */
export function periodKeyOr(value: string | null | undefined, fallback: PeriodKey = 'month'): PeriodKey {
  return value && (PERIOD_KEYS as string[]).includes(value) ? (value as PeriodKey) : fallback;
}

/** Whole days since a date (0 = today), null when there is none. */
export function daysSince(value: string | null | undefined): number | null {
  const d = parseDate(value || null);
  if (!d) return null;
  return Math.max(0, Math.round((startOfDay(new Date()).getTime() - startOfDay(d).getTime()) / 86400000));
}

// ---------- Rounding ----------

export const INCREMENTS = [1, 5, 6, 10, 15, 30, 60];

export const DEFAULT_CLIENT_ROUNDING: RoundingSettings = {
  rounding_enabled: false,
  rounding_mode: 'nearest',
  rounding_increment: 15,
  rounding_threshold: null,
  apply_scope: 'per_entry',
  min_billable_time: 0,
  grace_period: 0,
  show_original_time: false
};

/** Normalise stored settings for the form ('none' = disabled, mode defaults to nearest). */
export function roundingForm(stored: Partial<RoundingSettings> | null | undefined): RoundingSettings {
  const s = { ...DEFAULT_CLIENT_ROUNDING, ...(stored || {}) };
  const enabled = !!s.rounding_enabled && s.rounding_mode !== 'none';
  return {
    ...s,
    rounding_enabled: enabled,
    rounding_mode: s.rounding_mode === 'none' ? 'nearest' : s.rounding_mode,
    rounding_increment: Number(s.rounding_increment) || 15,
    rounding_threshold: s.rounding_threshold === null || s.rounding_threshold === undefined ? null : Number(s.rounding_threshold),
    min_billable_time: Number(s.min_billable_time) || 0,
    grace_period: Number(s.grace_period) || 0,
    show_original_time: !!s.show_original_time
  };
}

/** Same rules as computeRoundedDuration() on the server. */
export function roundMinutes(minutes: number, s: RoundingSettings): number {
  const original = Math.max(0, Math.round(minutes));
  if (!s.rounding_enabled || s.rounding_mode === 'none') return original;
  const inc = Math.max(1, Number(s.rounding_increment) || 15);
  let working = original;
  const grace = Number(s.grace_period) || 0;
  if (grace > 0) {
    const rem = working % inc;
    if (rem > 0 && rem <= grace) working -= rem;
  }
  let rounded: number;
  switch (s.rounding_mode) {
    case 'up': rounded = Math.ceil(working / inc) * inc; break;
    case 'down': rounded = Math.floor(working / inc) * inc; break;
    case 'threshold': {
      const threshold = s.rounding_threshold ?? Math.ceil(inc / 2);
      const base = Math.floor(working / inc) * inc;
      rounded = working % inc >= threshold ? base + inc : base;
      break;
    }
    default: rounded = Math.round(working / inc) * inc;
  }
  rounded = Math.max(0, rounded);
  if (s.min_billable_time && rounded < s.min_billable_time) rounded = s.min_billable_time;
  return rounded;
}

/** The settings as the server expects them ('none' when disabled). */
export function roundingPayload(s: RoundingSettings): RoundingSettings {
  return {
    ...s,
    rounding_mode: s.rounding_enabled ? s.rounding_mode : 'none',
    rounding_threshold: s.rounding_mode === 'threshold' ? s.rounding_threshold ?? Math.ceil(s.rounding_increment / 2) : null
  };
}
